import {
  Box,
  Tabs,
  Tab,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  useMediaQuery,
  useTheme,
  Typography,
} from "@mui/material";
import { useState } from "react";
import { FontIcon, useThemeContext } from "@odl/core";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel({ children, value, index }: TabPanelProps) {
  return (
    <div hidden={value !== index} role="tabpanel">
      {value === index && (
        <Box sx={{ py: 3, px: { xs: 2, sm: 0 } }}>{children}</Box>
      )}
    </div>
  );
}

interface AppHeaderProps {
  value: number;
  onChange: (event: React.SyntheticEvent, newValue: number) => void;
}

export function AppHeader({ value, onChange }: AppHeaderProps) {
  const { mode, setMode } = useThemeContext();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [drawerOpen, setDrawerOpen] = useState(false);

  const menuItems = ["Features", "Timeline", "Product Team"];

  const handleMenuItemClick = (index: number) => {
    onChange({} as React.SyntheticEvent, index);
    setDrawerOpen(false);
  };

  const toggleDrawer = (open: boolean) => {
    setDrawerOpen(open);
  };

  const toggleTheme = () => {
    setMode?.(mode === "light" ? "dark" : "light");
  };

  return (
    <Box
      component="header"
      sx={{
        position: "sticky",
        top: 0,
        zIndex: 10,
        bgcolor: "background.paper",
        borderBottom: 1,
        borderColor: "background.default",
        paddingTop: 1,
        overflowX: "hidden",
        "::before": {
          content: "''",
          position: "absolute",
          top: "0",
          left: "0",
          width: "200%",
          height: "5px",
          background:
            "linear-gradient(to left,  #0347bf, #00aba7, #77bc4a, #00aba7, #0347bf)",
          backgroundSize: "100% 100%",
          zIndex: 11,
        },
      }}
    >
      <Box
        sx={{
          maxWidth: 1200,
          mx: "auto",
          px: { xs: 2, sm: 3 },
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          minHeight: 56,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <svg
            viewBox="0 0 2474.27 258.73"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-label="Objective Intelligence"
            width={isMobile ? "200" : "269"}
            height={isMobile ? "20" : "27"}
          >
            <defs>
              <style>{`
                .cls-1 { fill: ${theme.palette.mode === "dark" ? theme.palette.text.primary : theme.palette.primary.light}; }
                .cls-2, .cls-3 { fill: ${theme.palette.text.primary}; }
                .cls-4 { fill: url(#linear-gradient); }
                .cls-3 { fill-rule: evenodd; }
                .cls-5 { fill: #fff; }
              `}</style>
              <linearGradient
                id="linear-gradient"
                x1="192.24"
                y1="250.83"
                x2="37.81"
                y2="-16.65"
                gradientUnits="userSpaceOnUse"
              >
                <stop offset="0" stopColor="#77bc4a" />
                <stop offset=".49" stopColor="#00aba7" />
                <stop offset=".94" stopColor="#0347bf" />
              </linearGradient>
            </defs>
            <path
              className="cls-4"
              d="M222.69,2.06H7.36C3.31,2.06,0,5.38,0,9.43v215.33c0,4.05,3.31,7.36,7.36,7.36h215.33c4.05,0,7.36-3.31,7.36-7.36V9.43c0-4.05-3.31-7.36-7.36-7.36Z"
            />
            <g>
              <path
                className="cls-2"
                d="M734.82,177.77c0,20.91,10.54,49.18,57.12,49.18s57.12-28.27,57.12-49.18v-35.06h-46.58v38.42c0,6.12,0,14.49-10.56,14.49s-10.56-8.38-10.56-14.49v-90.65c0-6.13,0-14.51,10.56-14.51s10.56,8.38,10.56,14.51v25.47h46.58v-22.12c0-20.93-10.58-49.18-57.12-49.18s-57.12,28.25-57.12,49.18v83.94Z"
              />
              <path
                className="cls-2"
                d="M862.52,193.51c0,27.89,15.47,31.23,35.5,31.23h11.08V81.16h11.61v-34.6h-11.61V1.95h-46.58v44.61h-11.66v34.6h11.66v112.35Z"
              />
              <path
                className="cls-3"
                d="M927.22,224.9h46.58V46.68h-46.58v178.22ZM927.22,34.96h46.58V2.06h-46.58v32.92-.02Z"
              />
              <path
                className="cls-3"
                d="M1008.48,224.9h54.47l31.64-178.22h-46.56l-11.63,114.06h-.56l-10.82-114.06h-47.94l31.39,178.22Z"
              />
              <path
                className="cls-3"
                d="M611.02,177.77c0,20.91,10.54,49.18,57.1,49.18s57.16-28.25,57.16-49.18v-18.96h-46.6v22.32c0,6.12,0,14.49-10.56,14.49s-10.56-8.38-10.56-14.49v-38.22h67.71v-49.08c0-20.93-10.56-49.18-57.16-49.18s-57.1,28.25-57.1,49.18v83.96-.02ZM657.56,90.45c0-6.13,0-14.49,10.56-14.49s10.56,8.36,10.56,14.49v19.54h-21.11v-19.54Z"
              />
              <path
                className="cls-3"
                d="M1092.94,177.77c0,20.91,10.54,49.18,57.11,49.18s57.13-28.25,57.13-49.18v-18.96h-46.56v22.32c0,6.12,0,14.49-10.56,14.49s-10.56-8.38-10.56-14.49v-38.22h67.7v-49.08c0-20.93-10.56-49.18-57.14-49.18s-57.11,28.25-57.11,49.18v83.96l-.02-.02ZM1139.5,90.45c0-6.13,0-14.51,10.56-14.51s10.56,8.38,10.56,14.51v19.54h-21.14v-19.54"
              />
              <path
                className="cls-3"
                d="M338.13,58.41c0-7.54,0-20.37,14.37-20.37s14.35,12.83,14.35,20.37v110.17c0,5.86,0,20.33-14.35,20.33s-14.37-14.47-14.37-20.33V58.41ZM288.32,164.11c0,39.61,24.63,62.84,64.16,62.84s64.14-23.23,64.14-62.84V62.86c0-43.52-27.06-62.86-64.14-62.86s-64.16,19.34-64.16,62.86v101.25Z"
              />
              <path
                className="cls-3"
                d="M475.31,180.88c0,6.14,0,14.51,10.56,14.51s10.58-8.38,10.58-14.51v-90.65c0-6.13,0-14.51-10.58-14.51s-10.56,8.36-10.56,14.51l-.12,90.65h.12ZM475.19,221.48v3.3h-46.56V1.95h46.56v46.93c3.39-2.44,10.42-4.49,14.21-4.49,43.65,0,53.59,28.27,53.59,49.18v83.94c0,20.9-9.6,49.18-52.25,49.18-3.79,0-12.15-1.73-15.59-5.22h.04"
              />
              <path
                className="cls-2"
                d="M553.86,258.57l11.02.16c32.77,0,35.56-19.93,35.56-46.72V46.67h-46.56v211.89"
              />
              <path
                className="cls-2"
                d="M553.86,2.06h46.58v32.9h-46.58V2.06Z"
              />
            </g>
            <g>
              <path
                className="cls-1"
                d="M1268.96,226.68V44.02h17.88v182.66h-17.88Z"
              />
              <path
                className="cls-1"
                d="M1403.63,226.68l-62.9-151.96h-.77v151.96h-16.86V44.02h22.22l62.58,151.96h.77V44.02h16.35v182.66h-21.39Z"
              />
              <path
                className="cls-1"
                d="M1498.07,59.5v167.18h-17.62V59.5h-34.74v-15.48h87.35v15.48h-34.99Z"
              />
              <path
                className="cls-1"
                d="M1553.75,226.68V44.02h72.79v16h-55.43v63.98h51.85v15.48h-51.85v70.95h57.98v16.25h-75.35Z"
              />
              <path
                className="cls-1"
                d="M1660.52,226.68V44.02h17.88v165.89h51.6v16.77h-69.47Z"
              />
              <path
                className="cls-1"
                d="M1751.7,226.68V44.02h17.88v165.89h51.6v16.77h-69.47Z"
              />
              <path
                className="cls-1"
                d="M1842.88,226.68V44.02h17.88v182.66h-17.88Z"
              />
              <path
                className="cls-1"
                d="M1979.66,226.05c-7.07,2.66-15.37,3.99-24.9,3.99-10.05,0-19.07-2.24-27.07-6.71-8.01-4.47-14.73-10.84-20.18-19.09-5.45-8.26-9.62-18.23-12.52-29.93-2.9-11.69-4.34-24.77-4.34-39.22,0-13.59,1.4-26.23,4.21-37.93,2.81-11.69,6.85-21.8,12.13-30.31,5.28-8.51,11.75-15.18,19.41-19.99,7.66-4.81,16.35-7.22,26.05-7.22,8.85,0,16.94,1.81,24.27,5.42,7.32,3.61,13.45,9.29,18.39,17.03l-11.49,11.87c-3.92-6.02-8.47-10.53-13.67-13.55-5.2-3.01-10.77-4.51-16.73-4.51-7.5,0-13.96,2.11-19.41,6.32-5.45,4.22-10.01,9.93-13.67,17.16-3.66,7.22-6.38,15.61-8.17,25.15-1.79,9.55-2.68,19.74-2.68,30.57s.93,21.85,2.81,31.48c1.87,9.63,4.72,17.93,8.56,24.9,3.83,6.97,8.6,12.43,14.3,16.38,5.7,3.96,12.3,5.93,19.79,5.93,5.45,0,10.35-.69,14.69-2.06,4.34-1.37,7.96-3.35,10.86-5.93v-61.92h-25.8v-15.48h43.16v86.98c-4.94,4.46-10.94,8.02-18.01,10.68Z"
              />
              <path
                className="cls-1"
                d="M2031.64,226.68V44.02h72.79v16h-55.43v63.98h51.85v15.48h-51.85v70.95h57.98v16.25h-75.35Z"
              />
              <path
                className="cls-1"
                d="M2218.92,226.68l-62.9-151.96h-.77v151.96h-16.86V44.02h22.22l62.58,151.96h.77V44.02h16.35v182.66h-21.39Z"
              />
              <path
                className="cls-1"
                d="M2355.51,224.62c-7.32,4.3-15.58,6.45-24.78,6.45-9.88,0-18.64-2.45-26.31-7.35-7.66-4.9-14.13-11.61-19.41-20.12-5.28-8.51-9.32-18.62-12.13-30.32-2.81-11.69-4.21-24.34-4.21-37.93s1.4-26.49,4.21-38.18c2.81-11.69,6.89-21.8,12.26-30.31,5.36-8.51,11.88-15.18,19.54-19.99,7.66-4.81,16.35-7.22,26.05-7.22,8.51,0,16.47,1.81,23.88,5.42,7.41,3.61,13.58,9.29,18.52,17.03l-11.49,11.87c-3.92-6.02-8.47-10.49-13.67-13.42-5.2-2.92-10.69-4.39-16.47-4.39-7.49,0-14.01,2.06-19.54,6.19-5.54,4.13-10.13,9.8-13.79,17.03-3.66,7.22-6.38,15.65-8.17,25.28-1.79,9.63-2.68,19.87-2.68,30.7s.93,21.03,2.81,30.57c1.87,9.55,4.64,17.93,8.3,25.16,3.66,7.22,8.26,12.94,13.79,17.16,5.53,4.22,12.04,6.32,19.54,6.32,6.47,0,12.34-1.85,17.62-5.55,5.28-3.7,9.62-8.55,13.03-14.58l11.24,11.61c-4.77,8.09-10.81,14.28-18.13,18.58Z"
              />
              <path
                className="cls-1"
                d="M2398.92,226.68V44.02h72.79v16h-55.43v63.98h51.85v15.48h-51.85v70.95h57.98v16.25h-75.35Z"
              />
            </g>
            <g>
              <rect
                className="cls-5"
                x="140.92"
                y="55.03"
                width="36.52"
                height="36.51"
                rx="1.7"
                ry="1.7"
              />
              <path
                className="cls-5"
                d="M140.95,129.32c0,7-5.73,12.73-12.73,12.73h-25.09c-7,0-12.73-5.73-12.73-12.73v-25.09c0-7,5.73-12.73,12.73-12.73h26.63c.7,0,1.27-.57,1.27-1.27v-33.98c0-.7-.57-1.27-1.27-1.27h-44.75c-17.11,0-31.12,14-31.12,31.12v61.23c0,17.17,14.05,31.22,31.22,31.22h61.23c17.11,0,31.12-14,31.12-31.12v-44.75c0-.7-.57-1.27-1.27-1.27h-33.98c-.7,0-1.27.57-1.27,1.27v26.63Z"
              />
            </g>
          </svg>
        </Box>

        {/* Desktop Navigation */}
        {!isMobile && (
          <Tabs
            value={value}
            onChange={onChange}
            sx={{
              minHeight: 56,
              "& .MuiTabs-indicator": {
                bgcolor: "primary.main",
                height: 2,
              },
              "& .MuiTab-root": {
                minHeight: 56,
                textTransform: "none",
                fontWeight: 500,
                fontSize: "0.9rem",
              },
            }}
          >
            <Tab label="Features" />
            <Tab label="Timeline" />
            <Tab label="Product Team" />
          </Tabs>
        )}

        {/* Mobile & Desktop Controls */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <IconButton
            onClick={toggleTheme}
            aria-label={`Switch to ${mode === "dark" ? "light" : "dark"} mode`}
          >
            {mode === "dark" ? (
              <FontIcon name="i-weather-moon" />
            ) : (
              <FontIcon name="i-toggle-light" />
            )}
          </IconButton>

          {isMobile && (
            <IconButton
              onClick={() => toggleDrawer(true)}
              aria-label="Open menu"
            >
              <FontIcon className="i-navigation-menu" />
            </IconButton>
          )}
        </Box>
      </Box>

      {/* Mobile Drawer Menu */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => toggleDrawer(false)}
        sx={{
          "& .MuiDrawer-paper": {
            width: 250,
            bgcolor: "background.paper",
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            p: 2,
            borderBottom: 1,
            borderColor: "divider",
          }}
        >
          <Typography variant="h5">Menu</Typography>
          <IconButton onClick={() => toggleDrawer(false)} size="small">
            <FontIcon className="i-navigation-close" />
          </IconButton>
        </Box>

        <List>
          {menuItems.map((item, index) => (
            <ListItem key={item} disablePadding>
              <ListItemButton
                selected={value === index}
                onClick={() => handleMenuItemClick(index)}
                sx={{
                  "&.Mui-selected": {
                    bgcolor: "action.selected",
                    "&:hover": {
                      bgcolor: "action.selected",
                    },
                  },
                }}
              >
                <ListItemText
                  primary={item}
                  primaryTypographyProps={{
                    fontWeight: value === index ? 600 : 400,
                  }}
                />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </Drawer>
    </Box>
  );
}

export { TabPanel };
